// src/oura/delivery/AddAsDeliverable.tsx
//
// Site issues: a problem outside the contract stays here and on the handover
// scorecard, and never blocks Done. When the client takes it on, staff add it
// as a deliverable: a custom line (X) on the contract whose covers are this
// check, so it becomes an item under its own deliverable in Delivery (H27).
// The contract edit is the same one the Plan view makes.

import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { getEngagement, updateEngagement, DEMO_STAFF, type Engagement } from "@service/aeo/clientReview";
import { cn } from "../lib/cn";
import { DrawerBlock, PillButton, Tag, TYPE } from "../ui";
import { OURA_ROOT } from "../nav";
import { FIELD } from "./drawerParts";

/** `again`: a read after a failed one, so the failure stays on screen with its button busy. */
type Load = { state: "loading"; again?: boolean } | { state: "none" } | { state: "failed" } | { state: "ready"; e: Engagement };

/** The server's refusal when the contract changed since this drawer read it (H27). */
function isChangedRefusal(err: unknown): boolean {
    const res = (err as { response?: { status?: number; data?: { errors?: unknown[] } } }).response;
    return res?.status === 409 && Array.isArray(res.data?.errors) && res.data?.errors[0] === "engagement_changed";
}

/** The check in the client's words, first letter lower case: "Fixing thin pages across your whole site". */
function clientWords(label: string): string {
    const plain = label.replace(/ on the key pages$/i, "").trim();
    return /^(AI|Google|SEO)\b/.test(plain) ? plain : plain.charAt(0).toLowerCase() + plain.slice(1);
}

function nextCustomCode(e: Engagement): string {
    const used = e.deliverables.map((d) => d.code.match(/^X(\d+)$/i)).filter(Boolean).map((m) => Number(m![1]));
    return `X${(used.length ? Math.max(...used) : 0) + 1}`;
}

export function AddAsDeliverable({ businessId, checkKey, checkLabel }: { businessId: string; checkKey: string; checkLabel: string }) {
    const [load, setLoad] = useState<Load>({ state: "loading" });
    const [open, setOpen] = useState(false);
    const [label, setLabel] = useState(checkLabel);
    const [month, setMonth] = useState("");
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [added, setAdded] = useState<string | null>(null);
    const [attempt, setAttempt] = useState(0);

    useEffect(() => {
        let live = true;
        getEngagement(businessId)
            .then((e) => live && setLoad(e ? { state: "ready", e } : { state: "none" }))
            // A failed read is not "no contract": it says so, with a way to read it again.
            .catch(() => live && setLoad({ state: "failed" }));
        return () => {
            live = false;
        };
    }, [businessId, attempt]);

    if (load.state === "failed" || (load.state === "loading" && load.again)) {
        return (
            <DrawerBlock label="Contract">
                <p role="alert" className={cn(TYPE.tc, "m-0 text-gray-600")}>
                    The contract did not load, so this cannot be added as a deliverable yet.
                </p>
                <div className="mt-2">
                    <PillButton
                        busy={load.state === "loading"}
                        onClick={() => {
                            setLoad({ state: "loading", again: true });
                            setAttempt((n) => n + 1);
                        }}
                    >
                        Try again
                    </PillButton>
                </div>
            </DrawerBlock>
        );
    }
    if (load.state !== "ready") return null;
    const e = load.e;
    const covering = e.deliverables.filter((d) => (d.covers ?? []).includes(checkKey));
    const covered = covering.length > 0;
    const coveredNote = covered ? (
        <>
            <span className="flex flex-wrap items-center gap-1.5">
                {covering.map((d) => (
                    <Tag key={d.code} tone="blue">
                        {d.code} {d.label}
                    </Tag>
                ))}
            </span>
            <p className={cn(TYPE.tc, "m-0 mt-1 text-gray-500")}>
                Delivery tracks this check under {covering.map((d) => d.code).join(" and ")}, on the 10 key pages. Pages beyond them are outside the contract.
            </p>
        </>
    ) : null;

    const code = nextCustomCode(e);
    const save = async () => {
        const m = Number(month) || null;
        if (!label.trim()) {
            setError("Give it a name the client will read.");
            return;
        }
        if (!m || m < 1 || m > e.termMonths) {
            setError(`Pick a month from 1 to ${e.termMonths}.`);
            return;
        }
        setBusy(true);
        setError(null);
        try {
            const seeds = e.deliverables.map((d) => ({ ...d }));
            // The client reads the note and the change log: client words, no code, no "Site issues" (H27).
            await updateEngagement(
                businessId,
                {
                    deliverables: [...seeds, { code, label: label.trim(), months: [m], quantity: null, cadence: "once", note: `Fixing ${clientWords(checkLabel)} across your whole site`, covers: [checkKey] }],
                    expectedUpdatedAt: e.updatedAt ?? null,
                },
                DEMO_STAFF.name,
                `Added to your plan: ${label.trim()}, month ${m}.`,
            );
            setAdded(`${code}-m${m}`);
            setOpen(false);
        } catch (err) {
            if (isChangedRefusal(err)) {
                // Read the contract again, so the add is offered on what is there now.
                const fresh = await getEngagement(businessId).catch(() => null);
                if (fresh) setLoad({ state: "ready", e: fresh });
                setError("Someone changed this contract a moment ago. It is reloaded now: add the line again.");
            } else setError("The contract did not save. Try again.");
        } finally {
            setBusy(false);
        }
    };

    return (
        <DrawerBlock label={covered ? "In the contract" : "Outside the contract"}>
            {coveredNote}
            {added ? (
                <p className="m-0">
                    Added as {added.split("-m")[0]}.{" "}
                    <Link className="font-medium text-blue-700 hover:underline" to={`${OURA_ROOT}/businesses/${encodeURIComponent(businessId)}/delivery?d=${encodeURIComponent(added)}`}>
                        Open it in Delivery
                    </Link>
                </p>
            ) : !open ? (
                <>
                    {!covered && (
                        <p className="m-0 text-gray-600">No line on the contract covers this check. It stays here and on the handover scorecard, and never blocks Done.</p>
                    )}
                    <div className="mt-2">
                        <PillButton writes onClick={() => setOpen(true)}>{covered ? "Add the other pages as a deliverable" : "Add as a deliverable"}</PillButton>
                    </div>
                </>
            ) : (
                <form
                    className="space-y-3"
                    onSubmit={(ev) => {
                        ev.preventDefault();
                        void save();
                    }}
                >
                    <p className={cn(TYPE.tc, "m-0 text-gray-600")}>
                        A custom line, {code}, on the contract. Its item is this check, so Oura closes it on its scan.
                    </p>
                    <label className="block">
                        <span className={TYPE.lab}>What the client reads</span>
                        <input className={cn(FIELD, "mt-1")} value={label} onChange={(ev) => setLabel(ev.target.value)} maxLength={120} autoFocus />
                    </label>
                    <label className="block">
                        <span className={TYPE.lab}>Plan month</span>
                        <input className={cn(FIELD, "mt-1 w-28")} value={month} onChange={(ev) => setMonth(ev.target.value.replace(/[^0-9]/g, ""))} inputMode="numeric" placeholder={`1 to ${e.termMonths}`} />
                    </label>
                    {error && (
                        <p role="alert" className={cn(TYPE.tc, "m-0 text-rose-600")}>
                            {error}
                        </p>
                    )}
                    <div className="flex gap-2">
                        <PillButton type="submit" variant="primary" busy={busy} writes>
                            Add {code}
                        </PillButton>
                        <PillButton onClick={() => setOpen(false)} disabled={busy}>
                            Cancel
                        </PillButton>
                    </div>
                </form>
            )}
        </DrawerBlock>
    );
}
